import { U, type Usage } from './hid';

/**
 * What a headset's buttons mean right now, and which of its lights are on (1.13.0, Teampace 3141).
 * Pure: the device's button values and Stitch's situation in, one thing to do out.
 *
 * The rules (the ones Microsoft Teams' certified headsets expect):
 * - Something rings and nothing is on: the call button (hook switch going off the hook) answers;
 *   drop, or Jabra's reject (line busy tone), declines.
 * - On a call, Patch or meeting: the call button going back on the hook ends it (leaves a Patch or
 *   meeting); drop does the same; mute toggles mute; flash holds or resumes a call.
 * - A hook switch that reports a state reacts to its changes; one that reports presses toggles.
 * - Lights follow Stitch, never the other way round: off-hook while on, ring while ringing, mute,
 *   hold. Setting off-hook keeps the headset's own idea of on and off the hook in step (Poly needs
 *   this, or a hang-up takes two presses), and its echo of that change does nothing here.
 */

export type Press = 'answer' | 'decline' | 'hangup' | 'mute' | 'hold';

export interface Situation {
  /** A call or Patch rings for this person, here. */
  ringing: boolean;
  /** A call, Patch or meeting this device has the sound for (a call still dialing counts). */
  active: boolean;
  muted: boolean;
  /** The call is on hold. */
  held: boolean;
  /** Hold applies (a call; not a Patch or meeting). */
  canHold: boolean;
}

export const IDLE: Situation = { ringing: false, active: false, muted: false, held: false, canHold: false };

export class HeadsetLogic {
  private last = new Map<Usage, number>();

  /** `hookIsState`: the hook switch reports on/off the hook (most headsets), not presses. */
  constructor(private readonly hookIsState = true) {}

  /** A report's buttons: what to do, if anything. */
  press(values: ReadonlyMap<Usage, number>, s: Situation): Press | null {
    const rose = (u: Usage) => {
      const v = values.get(u);
      if (v === undefined) return false;
      const before = this.last.get(u) ?? 0;
      return v !== 0 && before === 0;
    };
    const fell = (u: Usage) => {
      const v = values.get(u);
      if (v === undefined) return false;
      const before = this.last.get(u) ?? 0;
      return v === 0 && before !== 0;
    };
    let out: Press | null = null;
    if (this.hookIsState) {
      if (rose(U.hookSwitch) && s.ringing && !s.active) out = 'answer';
      else if (fell(U.hookSwitch) && s.active) out = 'hangup';
    } else if (rose(U.hookSwitch)) {
      if (s.ringing && !s.active) out = 'answer';
      else if (s.active) out = 'hangup';
    }
    if (!out && rose(U.drop)) out = s.active ? 'hangup' : s.ringing ? 'decline' : null;
    if (!out && rose(U.lineBusyTone) && s.ringing && !s.active) out = 'decline';
    if (!out && rose(U.phoneMute) && s.active) out = 'mute';
    if (!out && rose(U.flash) && s.active && s.canHold) out = 'hold';
    for (const [u, v] of values) this.last.set(u, v);
    return out;
  }

  /**
   * Stitch set the off-hook light: the headset's hook switch follows it (and may say so), so that
   * value is what it already is here (its echo isn't a press).
   */
  synced(offHook: boolean): void {
    if (this.hookIsState) this.last.set(U.hookSwitch, offHook ? 1 : 0);
  }
}

/** The lights for a situation. */
export function lightsFor(s: Situation): Map<Usage, number> {
  return new Map<Usage, number>([
    [U.ledOffHook, s.active ? 1 : 0],
    [U.ledRing, s.ringing ? 1 : 0],
    [U.ringer, s.ringing && !s.active ? 1 : 0],
    [U.ledMute, s.active && s.muted ? 1 : 0],
    [U.ledHold, s.active && s.held ? 1 : 0]
  ]);
}

export const sameLights = (a: ReadonlyMap<Usage, number> | null, b: ReadonlyMap<Usage, number>) => Boolean(a) && [...b].every(([u, v]) => a!.get(u) === v);
